import moment from 'moment';
import * as React from 'react';
import { injectIntl, FormattedMessage, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { Accordion } from 'semantic-ui-react';

import { ErrorBoundary } from '../../../containers/ErrorBoundary';
import { localeData } from '../../../locales/data';
import { LEARN_ALPHA_SUBMISSION_PAGE_PATH } from '../../../shared/constants';
import { alphaColumnsSelfCorrelation } from '../../../shared/data/AlphaDetailsSummaryColumns';
import { addModifiers, changeSmallNumberFormat, getLanguageCode } from '../../../shared/helpers/index';
import { ToolTip } from '../../ToolTip/';
import { Loader } from '../../Loader';

import './Correlation.scss';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { formatNumber, FormatNumberValueType } from '../../../shared/helpers/formatNumber';
import { getIsPowerPoolAlphaStatus } from '../../../shared/helpers/AlphaPropertiesValidation';

const HighchartsReact = React.lazy(() => import('../../../shared/helpers/highcharts'));

const CORRELATION_NUM_FRACTION_DIGITS = 4;

interface CorrelationProps {
  isProdAlphas: boolean;
  alpha?: any;
  alfaId?: string;
  id?: string;
  refreshCorrelationSelf: (e: any) => void;
  refreshCorrelationPowerPool: (e: any) => void;
  refreshCorrelationProd: (e: any) => void;
  fetchingCorrelationSelf: boolean;
  fetchingCorrelationPowerPool: boolean;
  fetchingCorrelationProd: boolean;
  settingsOptions?: any;
}

interface CorrelationState {
  prodShow: boolean;
  selfShow: boolean;
  powerPoolShow: boolean;
}

type CorrelationType = 'prod' | 'self' | 'powerPool';

interface CorrelationMainProps {
  type: CorrelationType;
  maxCorrelation: number | undefined;
  minCorrelation: number | undefined;
  correlationDate: moment.Moment;
  correlationStatusCode?: number;
  correlationInfo: any;
  showContent: boolean;
  handlerShowAccordion: (e: any) => void;
  myAlphaRecord?: any;
  refreshCorrelation: (e: any) => void;
  fetchingCorrelation: boolean;
  settingsOptions?: any;
}

interface CorrelationTableProps {
  title: string;
  properties: any[];
  rows: any[][];
  currentRow: any[][];
  settingsOptions?: any;
}

export interface SelfCorrelationRow {
  name?: string;
  universe: string;
  correlation: number;
  sharpe: number;
  turnover: number;
  fitness: number;
  returns: number;
  margin: number;
}

type CorrelationMainPropsGeneric = CorrelationMainProps;

export const getAlphaCorrelationDetails = (activeAlphaInfo: any): SelfCorrelationRow => {
  const result: SelfCorrelationRow = {
    correlation: 0,
    fitness: 0,
    margin: 0,
    name: '',
    returns: 0,
    sharpe: 0,
    turnover: 0,
    universe: '',
  };
  alphaColumnsSelfCorrelation.forEach(column => {
    if (column.alphaPath) {
      (result as any)[column.id as keyof SelfCorrelationRow] = activeAlphaInfo.getIn(column.alphaPath);
    } else if (column.id === 'correlation') {
      result[column.id] = 1; //self-correlation is always 1
    }
  });

  return result;
};

const getInstrumentTypeLabel = (settingsOptions: any, propertyName: string, record: string) => {
  const choices = settingsOptions.getIn([propertyName, 'choices']);
  if (choices) {
    const recordChoice = choices.find((choice: any) => choice.get('value') === record);

    return recordChoice ? recordChoice.get('label') : '';
  }

  return '';
};

function getFormattedNumber(type: string, name: string, value: number) {
  if (type === 'decimal' || type === 'number') {
    return formatNumber(value, type as FormatNumberValueType, {
      numFractionDigits: name === 'correlation' ? CORRELATION_NUM_FRACTION_DIGITS : undefined,
    });
  }

  return changeSmallNumberFormat({ value, type });
}

const CorrelationTable = ({ properties, rows, currentRow, settingsOptions }: CorrelationTableProps) => (
  <table cellSpacing="0" className="correlation__table">
    <thead>
      <tr className="correlation__table-properties">
        {properties.map((property: any, index: number) => (
          <th className={['percent', 'decimal', 'number', 'permyriad'].includes(property.type) ? 'number' : 'string'} key={index}>
            {property.title}
          </th>
        ))}
      </tr>
      {currentRow.map((line: any, key: number) => (
        <tr className="correlation__table-self-row" key={key}>
          {properties.map((val: any, index: number) => {
            if (val.name === 'name') {
              const name = line[index] ? (
                line[index]
              ) : (
                <FormattedMessageHelper tagName="span" id="app.anonymous" defaultMessage={localeData.en['app.anonymous']} />
              );

              return (
                <td key={index}>
                  {name} (<FormattedMessageHelper tagName="span" id="app.current" defaultMessage={localeData.en['app.current']} />)
                </td>
              );
            } else if (['percent', 'decimal', 'number', 'permyriad'].includes(val.type)) {
              const value = line[index] ? line[index] : 0;

              const formattedValue = getFormattedNumber(val.type, val.name, value);

              return (
                <td key={index} className="number">
                  {formattedValue}
                </td>
              );
            } else if (settingsOptions && val.name === 'instrumentType') {
              return <td key={index}>{getInstrumentTypeLabel(settingsOptions, val.name, line[index])}</td>;
            } else if (val.name === 'correlation') {
              return (
                <td key={index} className="number">
                  {formatNumber(1, val.type)}
                </td>
              );
            } else {
              return (
                <td className={['percent', 'decimal', 'number', 'permyriad'].includes(val.type) ? 'number' : 'string'} key={index}>
                  {line[index] && line[index].toLocaleString(getLanguageCode())}
                </td>
              );
            }
          })}
        </tr>
      ))}
      {currentRow.map((line: any, keyIndex: number) => (
        <tr className="correlation__table-white-row" key={keyIndex}>
          {line.map((_: any, index: number) => (
            <td key={index} />
          ))}
        </tr>
      ))}
    </thead>
    <tbody className="correlation__table-content">
      {rows.map((line: any, key: number) => (
        <tr key={key}>{line}</tr>
      ))}
    </tbody>
  </table>
);

const getConfig = (correlationInfo: any, intl: any) => {
  const indexMaxAlpha = correlationInfo.toJS().schema.properties.findIndex((property: any) => property.name === 'alphas');
  const indexMax = correlationInfo.toJS().schema.properties.findIndex((property: any) => property.name === 'max');
  const indexMin = correlationInfo.toJS().schema.properties.findIndex((property: any) => property.name === 'min');
  const config: any = {
    chart: {
      type: 'column',
      height: '331px',
    },
    credits: { enabled: false },
    legend: {
      enabled: false,
    },
    title: {
      text: '',
    },
    yAxis: {
      type: 'logarithmic',
      title: {
        text: `${intl.formatMessage({
          id: 'correlation.getConfig.config.1',
          defaultMessage: localeData.en['correlation.getConfig.config.1'],
        })}`,
        opposite: true,
      },
    },
  };
  const xAxis: any = {
    categories: [],
  };
  const series: any = {
    name: `${intl.formatMessage({
      id: 'correlation.getConfig.series.1',
      defaultMessage: localeData.en['correlation.getConfig.series.1'],
    })}`,
    color: '#47b74a',
    data: [],
  };

  correlationInfo.toJS().records.map((entry: any) => {
    xAxis.categories.push(
      `${entry[indexMin].toLocaleString(getLanguageCode(), { minimumFractionDigits: 1 })}…
        ${entry[indexMax].toLocaleString(getLanguageCode(), { minimumFractionDigits: 1 })}`
    );
    series.data.push(entry[indexMaxAlpha]);
  });
  config.xAxis = xAxis;
  config.series = [series];

  return config;
};

interface CorrelationSelfProps extends CorrelationProdProps {
  myAlphaRecord: any;
  settingsOptions: any;
}

const CorrelationSelf = ({
  correlationInfo,
  myAlphaRecord,
  fetchingCorrelation,
  maxCorrelation,
  correlationDate,
  settingsOptions,
  correlationStatusCode,
}: CorrelationSelfProps) => {
  if (fetchingCorrelation) {
    return <Loader active={true} inline="centered" size="massive" />;
  } else if (correlationInfo !== null && myAlphaRecord !== null && correlationDate !== null && typeof maxCorrelation === 'number') {
    const indexOfIdInProperty = correlationInfo.schema.properties.findIndex((val: any) => val.name === 'id');
    const columnsToDisplay = alphaColumnsSelfCorrelation.map(column => column.id);
    const newRows = correlationInfo.records.map((record: any) =>
      correlationInfo.schema.properties.map((property: any, indexProperty: any) => {
        if (!columnsToDisplay.includes(property.name)) {
          return undefined;
        } else if (property.name === 'id') {
          return undefined;
        } else if (property.name === 'name') {
          return (
            <td key={indexProperty}>
              <a
                className="correlation__table-content-link"
                href={`/alpha/${record[indexOfIdInProperty]}`}
                target="_blank"
                rel="noopener noreferrer"
                key={indexProperty}
              >
                {record[indexProperty] ? (
                  record[indexProperty]
                ) : (
                  <FormattedMessageHelper tagName="span" id="app.anonymous" defaultMessage={localeData.en['app.anonymous']} />
                )}
              </a>
            </td>
          );
        } else if (property.type === 'percent' || property.type === 'decimal' || property.type === 'permyriad') {
          const value = record[indexProperty] ? record[indexProperty] : 0;

          return (
            <td key={indexProperty} className="number">
              {getFormattedNumber(property.type, property.name, value)}
            </td>
          );
        } else if (record[indexProperty] && property.name === 'instrumentType') {
          return <td key={indexProperty}>{getInstrumentTypeLabel(settingsOptions, property.name, record[indexProperty])}</td>;
        } else {
          const val =
            typeof record[indexProperty] === 'number' ? record[indexProperty].toLocaleString(getLanguageCode()) : record[indexProperty];

          return (
            <td className={['percent', 'decimal', 'number', 'permyriad'].includes(property.type) ? 'number' : 'string'} key={indexProperty}>
              {val}
            </td>
          );
        }
      })
    );

    const displayedProperties = correlationInfo.schema.properties.filter((property: any) => columnsToDisplay.includes(property.name));

    return (
      <CorrelationTable
        title={correlationInfo.schema.title}
        properties={displayedProperties}
        rows={newRows.filter((val: any) => val !== undefined)}
        currentRow={[
          displayedProperties.map((property: any, index: any) => {
            if (property.name === 'name' && myAlphaRecord[property.name] === null) {
              return (
                <FormattedMessage
                  key={`anonymous-${index}`}
                  tagName="span"
                  id="app.anonymous"
                  defaultMessage={localeData.en['app.anonymous']}
                />
              );
            } else {
              return myAlphaRecord[property.name];
            }
          }),
        ]}
        settingsOptions={settingsOptions}
      />
    );
  } else if (correlationStatusCode === 412) {
    return <CorrelationErrorMessage messageId="correlation.self.invalidPnl" />;
  } else {
    return <CorrelationErrorMessage messageId="correlation.self.noResults" />;
  }
};

interface CorrelationProdProps {
  correlationInfo: any;
  fetchingCorrelation: boolean;
  maxCorrelation: number | undefined;
  correlationDate: any;
  correlationStatusCode?: number;
}
type CorrelationProdPropsGeneric = CorrelationProdProps & InjectedIntlProps;

const CorrelationProdIntl = ({
  correlationInfo,
  fetchingCorrelation,
  maxCorrelation,
  correlationDate,
  intl,
  correlationStatusCode,
}: CorrelationProdPropsGeneric) => {
  if (fetchingCorrelation) {
    return (
      <div className="loader">
        <Loader active={true} inline="centered" size="massive" />
      </div>
    );
  } else if (correlationInfo !== undefined && typeof maxCorrelation === 'number' && correlationDate !== null) {
    return (
      <ErrorBoundary isNotFullScreen={true}>
        <React.Suspense fallback={<Loader active={true} inline="centered" size="massive" />}>
          <HighchartsReact options={getConfig(correlationInfo, intl)} />
        </React.Suspense>
      </ErrorBoundary>
    );
  } else if (correlationStatusCode === 412) {
    return <CorrelationErrorMessage messageId="correlation.prod.invalidPnl" />;
  } else {
    return <CorrelationErrorMessage messageId="correlation.prod.noResults" />;
  }
};

interface CorrelationErrorMessageProps {
  messageId: string;
}

const CorrelationErrorMessage = ({ messageId }: CorrelationErrorMessageProps) => (
  <div className="correlation__no-results">
    <FormattedMessageHelper tagName="span" id={messageId} defaultMessage={localeData.en[messageId]} />
  </div>
);

const CorrelationProd = injectIntl(CorrelationProdIntl);

const CorrelationMain = ({
  type,
  maxCorrelation,
  minCorrelation,
  correlationDate,
  correlationInfo,
  showContent,
  handlerShowAccordion,
  myAlphaRecord,
  refreshCorrelation,
  fetchingCorrelation,
  settingsOptions,
  correlationStatusCode,
}: CorrelationMainPropsGeneric) => (
  <div className="correlation__content">
    <Accordion>
      <Accordion.Title className="correlation__content-status" active={showContent}>
        <div className="correlation__content-status-title">{<FormattedMessageHelper id={`alphaSummary.correlation.${type}`} />}</div>

        <div className="correlation__content-status-higher">
          <div className="correlation__content-status-higher-title">
            <FormattedMessageHelper tagName="span" id="correlation.correlationMain.maxCorrelation" />
            <ToolTip
              label={`tooltip.${type}.correlation.max.label`}
              tooltipInfo={`tooltip.${type}.correlation.max.content`}
              url={type === 'self' ? `${LEARN_ALPHA_SUBMISSION_PAGE_PATH}/#self-correlation` : undefined}
            />
          </div>
          <div className="correlation__content-status-higher-value">
            {typeof maxCorrelation !== 'number' && '-'}
            {typeof maxCorrelation === 'number' && getFormattedNumber('decimal', 'correlation', maxCorrelation)}
          </div>
        </div>

        <div className="correlation__content-status-higher">
          <div className="correlation__content-status-higher-title">
            <FormattedMessageHelper tagName="span" id="correlation.correlationMain.minCorrelation" />
            <ToolTip
              label={`tooltip.${type}.correlation.min.label`}
              tooltipInfo={`tooltip.${type}.correlation.min.content`}
              url={type === 'self' ? `${LEARN_ALPHA_SUBMISSION_PAGE_PATH}/#self-correlation` : undefined}
            />
          </div>
          <div className="correlation__content-status-higher-value">
            {typeof minCorrelation !== 'number' && '-'}
            {typeof minCorrelation === 'number' && getFormattedNumber('decimal', 'correlation', minCorrelation)}
          </div>
        </div>

        <div className="correlation__content-status-time">
          <div className="correlation__content-status-time-title">{`Last Run: ${
            correlationDate ? moment(correlationDate).format('ddd, L, LT') : '-'
          }`}</div>
          <div
            id={fetchingCorrelation ? 'correlation__refresh-symbol' : ''}
            className="correlation__content-status-time-refresh"
            onClick={refreshCorrelation}
          />
          <div
            onClick={handlerShowAccordion}
            className={addModifiers('correlation__content-status-arrow', showContent ? ['up'] : ['down'])}
          />
        </div>
      </Accordion.Title>
      <Accordion.Content active={showContent}>
        {type !== 'prod' && (
          <CorrelationSelf
            maxCorrelation={maxCorrelation}
            correlationDate={correlationDate}
            correlationInfo={correlationInfo}
            correlationStatusCode={correlationStatusCode}
            myAlphaRecord={myAlphaRecord}
            fetchingCorrelation={fetchingCorrelation}
            settingsOptions={settingsOptions}
          />
        )}
        {type === 'prod' && (
          <CorrelationProd
            maxCorrelation={maxCorrelation}
            correlationDate={correlationDate}
            correlationInfo={correlationInfo}
            correlationStatusCode={correlationStatusCode}
            fetchingCorrelation={fetchingCorrelation}
          />
        )}
      </Accordion.Content>
    </Accordion>
  </div>
);

class Correlation extends React.Component<CorrelationProps, CorrelationState> {
  constructor(props: CorrelationProps) {
    super(props);
    this.state = { prodShow: false, selfShow: false, powerPoolShow: false };
  }
  UNSAFE_componentWillReceiveProps(nextProps: CorrelationProps) {
    if (nextProps.alfaId !== this.props.alfaId) {
      this.setState({ prodShow: false, selfShow: false, powerPoolShow: false });
    }
  }

  handlerShowAccordionSelf = (e: any) => {
    if (!this.props.alpha.get('correlationSelf')) {
      this.props.refreshCorrelationSelf(e);
    }
    this.setState({ selfShow: !this.state.selfShow });
  };

  handlerShowAccordionPowerPool = (e: any) => {
    if (!this.props.alpha.get('correlationPowerPool')) {
      this.props.refreshCorrelationPowerPool(e);
    }
    this.setState({ powerPoolShow: !this.state.powerPoolShow });
  };

  handlerShowAccordionProd = (e: any) => {
    if (!this.props.alpha.get('correlationProd')) {
      this.props.refreshCorrelationProd(e);
    }
    this.setState({ prodShow: !this.state.prodShow });
  };

  render() {
    return (
      <div id={this.props.id} className="alphas-details-content__box">
        <div className="correlation__title">
          {' '}
          <FormattedMessageHelper tagName="span" id="correlation.1" defaultMessage={localeData.en['correlation.1']} />{' '}
        </div>
        <CorrelationMain
          type={'self'}
          correlationDate={this.props.alpha ? this.props.alpha.get('lastRunSelf') : null}
          correlationInfo={
            this.props.alpha && this.props.alpha.get('correlationSelf') ? this.props.alpha.get('correlationSelf').toJS() : null
          }
          showContent={this.state.selfShow}
          handlerShowAccordion={this.handlerShowAccordionSelf}
          myAlphaRecord={this.props.alpha ? getAlphaCorrelationDetails(this.props.alpha) : null}
          maxCorrelation={this.props.alpha?.getIn(['correlationSelf', 'max'])}
          minCorrelation={this.props.alpha?.getIn(['correlationSelf', 'min'])}
          correlationStatusCode={this.props.alpha?.getIn(['correlationStatusCode'])}
          refreshCorrelation={this.props.refreshCorrelationSelf}
          fetchingCorrelation={this.props.fetchingCorrelationSelf}
          settingsOptions={this.props.settingsOptions}
        />
        {getIsPowerPoolAlphaStatus(this.props.alpha) && (
          <CorrelationMain
            type={'powerPool'}
            correlationDate={this.props.alpha ? this.props.alpha.get('lastRunPowerPool') : null}
            correlationInfo={
              this.props.alpha && this.props.alpha.get('correlationPowerPool') ? this.props.alpha.get('correlationPowerPool').toJS() : null
            }
            showContent={this.state.powerPoolShow}
            handlerShowAccordion={this.handlerShowAccordionPowerPool}
            myAlphaRecord={this.props.alpha ? getAlphaCorrelationDetails(this.props.alpha) : null}
            maxCorrelation={this.props.alpha?.getIn(['correlationPowerPool', 'max'])}
            minCorrelation={this.props.alpha?.getIn(['correlationPowerPool', 'min'])}
            correlationStatusCode={this.props.alpha?.getIn(['correlationStatusCode'])}
            refreshCorrelation={this.props.refreshCorrelationPowerPool}
            fetchingCorrelation={this.props.fetchingCorrelationPowerPool}
            settingsOptions={this.props.settingsOptions}
          />
        )}
        {this.props.isProdAlphas && (
          <CorrelationMain
            type={'prod'}
            correlationDate={this.props.alpha ? this.props.alpha.get('lastRunProd') : null}
            correlationInfo={this.props.alpha && this.props.alpha.get('correlationProd') ? this.props.alpha.get('correlationProd') : null}
            showContent={this.state.prodShow}
            handlerShowAccordion={this.handlerShowAccordionProd}
            maxCorrelation={this.props.alpha?.getIn(['correlationProd', 'max'])}
            minCorrelation={this.props.alpha?.getIn(['correlationProd', 'min'])}
            correlationStatusCode={this.props.alpha?.getIn(['correlationStatusCode'])}
            refreshCorrelation={this.props.refreshCorrelationProd}
            fetchingCorrelation={this.props.fetchingCorrelationProd}
            settingsOptions={this.props.settingsOptions}
          />
        )}
      </div>
    );
  }
}

export default Correlation;
